import React, { useEffect, useRef, useState } from "react";

const Feedback = () => {
  const titleRef = useRef(null);
  const paraRef = useRef(null);
  const datadetailRef = useRef(null);
  const imgRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    platform: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const platforms = [
    "React",
    "Angular",
    "HTML",
    "Dot Net",
    "Python/Django",
    "Microsoft",
    "Azure",
    "AWS",
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          } else {
            entry.target.classList.remove("visible");
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    const elements = [
      titleRef.current,
      paraRef.current,
      datadetailRef.current,
      imgRef.current,
    ];
    elements.forEach((el) => el && observer.observe(el));

    return () => {
      elements.forEach((el) => el && observer.unobserve(el));
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBlur = (e) => {
    setTouched({ ...touched, [e.target.name]: true });
    validateField(e.target.name, e.target.value);
  };

  const validateField = (fieldName, value) => {
    let tempErrors = { ...errors };

    switch (fieldName) {
      case "name":
        if (!value.trim()) tempErrors.name = "First name is required.";
        else delete tempErrors.name;
        break;
      case "surname":
        if (!value.trim()) tempErrors.surname = "Surname is required.";
        else delete tempErrors.surname;
        break;
      case "email":
        if (!value.trim()) tempErrors.email = "Email is required.";
        else if (!/\S+@\S+\.\S+/.test(value))
          tempErrors.email = "Enter a valid email.";
        else delete tempErrors.email;
        break;
      case "platform":
        if (!value.trim())
          tempErrors.platform = "Please select a technology platform.";
        else delete tempErrors.platform;
        break;
      case "message":
        if (!value.trim()) tempErrors.message = "Message cannot be empty.";
        else delete tempErrors.message;
        break;
      default:
        break;
    }

    setErrors(tempErrors);
  };

  const validate = () => {
    let tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = "First name is required.";
    if (!formData.surname.trim()) tempErrors.surname = "Surname is required.";
    if (!formData.email.trim()) {
      tempErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = "Enter a valid email.";
    }
    if (!formData.platform.trim())
      tempErrors.platform = "Please select a technology platform.";
    if (!formData.message.trim())
      tempErrors.message = "Message cannot be empty.";

    setErrors(tempErrors);
    setTouched({
      name: true,
      surname: true,
      email: true,
      platform: true,
      message: true,
    });

    return Object.keys(tempErrors).length === 0;
  };

  const getFieldClasses = (fieldName) => {
    return `form-floating mb-3 ${
      touched[fieldName] && errors[fieldName] ? "has-error shake" : ""
    }`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);

      try {
        const response = await fetch("https://formspree.io/f/xjkezyjj", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          alert("Message sent successfully ✅");
          setFormData({
            name: "",
            surname: "",
            email: "",
            platform: "",
            message: "",
          });
          setTouched({});
        } else {
          alert("Failed to send ❌. Try again.");
        }
      } catch (error) {
        alert("Error ❌: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="container aboutBg next-section" id="contact">
      <div className="row">
        <div
          ref={titleRef}
          className="title_line text-center p-5 animate-title"
        >
          Reach Out to Us
        </div>

        {/* Left Side - Form */}
        <div className="col-md-7 order-2 order-md-1  about_data contactData">
          <form onSubmit={handleSubmit} className="p-3">
            <div className="row">
              <div className="col-12 col-lg-6 col-md-6 col-xl-6">
                {/* First Name */}
                <div className={getFieldClasses("name")}>
                  <input
                    type="text"
                    name="name"
                    id="floatingName"
                    className={`form-control ${
                      touched.name && errors.name ? "is-invalid" : ""
                    }`}
                    placeholder="First Name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="floatingName">First Name</label>
                  {touched.name && errors.name && (
                    <div className="invalid-feedback d-block">
                      {errors.name}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6 col-md-6 col-xl-6">
                {/* Surname */}
                <div className={getFieldClasses("surname")}>
                  <input
                    type="text"
                    name="surname"
                    id="floatingSurname"
                    className={`form-control ${
                      touched.surname && errors.surname ? "is-invalid" : ""
                    }`}
                    placeholder="Surname"
                    value={formData.surname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="floatingSurname">Surname</label>
                  {touched.surname && errors.surname && (
                    <div className="invalid-feedback d-block">
                      {errors.surname}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                {/* Email */}
                <div className={getFieldClasses("email")}>
                  <input
                    type="email"
                    name="email"
                    id="floatingEmail"
                    className={`form-control ${
                      touched.email && errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="floatingEmail">Email</label>
                  {touched.email && errors.email && (
                    <div className="invalid-feedback d-block">
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-12 dropFild">
                {/* Platform Dropdown */}
                <div className={getFieldClasses("platform")}>
                  <select
                    name="platform"
                    id="floatingPlatform"
                    className={`form-select ${
                      touched.platform && errors.platform ? "is-invalid" : ""
                    }`}
                    value={formData.platform}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Select Platform</option>
                    {platforms.map((p, idx) => (
                      <option key={idx} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="floatingPlatform">Technology Platform</label>
                  {touched.platform && errors.platform && (
                    <div className="invalid-feedback d-block">
                      {errors.platform}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-12 msg_wrapper">
                {/* Message */}
                <div className={getFieldClasses("message")}>
                  <textarea
                    name="message"
                    id="floatingMessage"
                    className={`form-control ${
                      touched.message && errors.message ? "is-invalid" : ""
                    }`}
                    placeholder="Leave a message"
                    style={{ height: "120px" }}
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  ></textarea>
                  <label htmlFor="floatingMessage">Message</label>
                  {touched.message && errors.message && (
                    <div className="invalid-feedback d-block">
                      {errors.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="reset"
                  className="btn btn-transparent"
                  disabled={loading}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Submit"}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Right Side - Image or Content */}
        <div className="col-md-5 order-1 order-md-2">
          <img
            ref={imgRef}
            className="img-fluid  mx-auto d-block animate-title"
            src="reachus.png"
            alt="Reach us"
          />
        </div>
      </div>
    </div>
  );
};

export default Feedback;
